import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LayoutDashboard,
  Send,
  MonitorPlay,
  BarChart2,
  CreditCard,
  Settings as SettingsIcon,
  Headphones,
  ChevronRight,
  ChevronsUpDown,
} from "lucide-react";
import images from "assets/img";
import usePermissions from "hooks/usePermissions";
import useMerchant from "hooks/useMerchant";

const WALLETS_LINKS = [
  { url: "/billing", label: "Billing" },
  { url: "/transactions", label: "Transactions" },
  { url: "/payout", label: "Payout" },
];

const SUPPORT_LINKS = [
  { url: "/support/help-center", label: "Help Center" },
  { url: "/support/contact-us", label: "Contact Us" },
];

export default function Sidebar() {
  const { sidebar, sidebarCollapsed } = useSelector((state) => state.misc);
  const { isPermitted } = usePermissions();
  const { merchant } = useMerchant();
  const { pathname } = useLocation();
  const [walletsOpen, setWalletsOpen] = useState(false);

  const isActive = (url) => pathname.split("/")[1] === url.split("/")[1];
  const walletsActive = WALLETS_LINKS.some((link) => isActive(link.url));

  const navLinks = [
    { url: "/dashboard", label: "Dashboard", Icon: LayoutDashboard, show: isPermitted?.view_all_overview },
    { url: "/promo", label: "Campaign", Icon: Send, show: isPermitted?.view_all_promos },
    { url: "/reward-ads", label: "Reward Ads", Icon: MonitorPlay, show: true },
    { url: "/reports", label: "Reports", Icon: BarChart2, show: true },
  ];

  const itemClass = (active) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
      active ? "text-primary bg-white font-semibold" : "text-body-text hover:bg-grey"
    }`;

  return (
    <aside
      className={`trans fixed top-16 lg:top-0 left-0 z-40 h-[calc(100vh-4rem)] lg:h-screen w-[272px] bg-[#F7F7FC] flex flex-col ${
        sidebar ? "left-0" : "-left-[272px]"
      } ${sidebarCollapsed ? "lg:-left-[272px]" : "lg:left-0"}`}
    >
      <div className="px-5 py-4 ">
        <button type="button" className="w-full flex items-center gap-3">
          <span className="w-8 h-8 rounded-lg bg-primary text-white font-semibold flex items-center justify-center overflow-hidden shrink-0">
            {merchant?.merchant_avatar ? (
              <img src={merchant.merchant_avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              merchant?.name?.[0]?.toUpperCase() || "M"
            )}
          </span>
          <span className="flex-1 text-left text-sm font-semibold text-dark-blue truncate">
            {merchant?.name || "Merchant"}
          </span>
          <ChevronsUpDown size={16} className="text-body-text shrink-0" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {navLinks.map(({ url, label, Icon, show }) => {
            if (!show) return null;
            return (
              <li key={url}>
                <Link to={url} className={itemClass(isActive(url))}>
                  <Icon size={18} />
                  <span>{label}</span>
                </Link>
              </li>
            );
          })}

          <li>
            <button
              type="button"
              onClick={() => setWalletsOpen((open) => !open)}
              className={`w-full ${itemClass(walletsActive)}`}
            >
              <CreditCard size={18} />
              <span className="flex-1 text-left">Wallets &amp; Billing</span>
              <ChevronRight size={16} className={`trans ${walletsOpen || walletsActive ? "rotate-90" : ""}`} />
            </button>

            {(walletsOpen || walletsActive) && (
              <ul className="mt-1 ml-[22px] pl-4 border-l border-border-lines space-y-1">
                {WALLETS_LINKS.map(({ url, label }) => (
                  <li key={url}>
                    <Link
                      to={url}
                      className={`block px-3 py-2 rounded-lg text-sm ${
                        isActive(url) ? "text-primary bg-focus font-semibold" : "text-body-text hover:bg-grey"
                      }`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>

        <p className="px-3 mt-6 mb-2 text-xs font-medium tracking-wide text-placeholder uppercase">
          Administration
        </p>

        <ul className="space-y-1">
          <li>
            <Link to="/settings/hub" className={itemClass(isActive("/settings"))}>
              <SettingsIcon size={18} />
              <span>Settings</span>
            </Link>
          </li>
          <li>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-body-text">
              <Headphones size={18} />
              <span>Support</span>
            </div>
            <ul className="mt-1 ml-[22px] pl-4 border-l border-border-lines space-y-1">
              {SUPPORT_LINKS.map(({ url, label }) => (
                <li key={url}>
                  <Link to={url} className="block px-3 py-2 rounded-lg text-sm text-body-text hover:bg-grey">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </nav>

      <div className="px-5 py-4 border-t border-border-lines flex items-center gap-3">
        <img src={images.Logo_sm} alt="" className="w-8 h-8 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-dark-blue truncate">ReachAfrika</p>
          <p className="text-xs text-body-text">Merchant v1.0.1</p>
        </div>
      </div>
    </aside>
  );
}
